import React, { forwardRef, Ref } from "react";
import { isSameDomain, isInternalDomain } from "@stadium/helpers/routes";

import Loader from "@stadium/ui/src/components/loaders/loader";

import defaultStyles from "./main.module.scss";

type ButtonStyle =
  | "primary"
  | "primary-outline"
  | "link"
  | "danger"
  | "danger-outline"
  | "success"
  | "success-outline"
  | "light"
  | "rounded-btn"
  | "icon-btn"
  | "no-style";

export interface ExternalButtonProps
  extends React.DetailedHTMLProps<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    HTMLAnchorElement
  > {
  loaderType?: any;
  isLoading?: boolean;
  loaderOptions?: Record<string, unknown>;
  btnStyle?: ButtonStyle;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  className?: string;
  styles?: Record<string, string>;
  href: string;
  target?: string;
  rel?: string;
}

const getButtonBaseClassNames = (btnStyle: ButtonStyle, styles = defaultStyles): string => {
  switch (btnStyle) {
    case "primary":
      return styles.btnPrimary;

    case "primary-outline":
      return styles.btnOutlinePrimary;

    case "link":
      return styles.btnLink;

    case "danger":
      return styles.btnDanger;

    case "danger-outline":
      return styles.btnOutlineDanger;

    case "success":
      return styles.btnSuccess;

    case "success-outline":
      return styles.btnOutlineSuccess;

    case "light":
      return styles.btnLight;

    case "rounded-btn":
      return styles.roundedBtn;

    case "icon-btn":
      return styles.iconBtn;

    case "no-style":
      return "";

    default:
      throw new Error("Undefined button btn style. Please create one");
  }
};

// NOTE:-
// 1. Use it when you want to perform external app navigation and optionally non-navigation actions on click
// 2. for disabled css, pass ".disabled" class (ref - https://getbootstrap.com/docs/4.6/components/buttons/#disabled-state)

const ExternalButton = forwardRef(
  (
    {
      loaderType = "dotsLoader",
      isLoading = false,
      loaderOptions,
      btnStyle = "primary",
      children = "",
      onClick,
      className = "",
      styles = defaultStyles,
      target,
      href,
      ...restProps
    }: ExternalButtonProps,
    ref: Ref<HTMLAnchorElement>,
  ) => {
    const linkTarget = (() => {
      if (typeof target !== "undefined") {
        return target;
      }
      return isSameDomain({ url: href }) ? "" : "_blank";
    })();

    const allClassNames = `${getButtonBaseClassNames(btnStyle, styles)} ${className}`;
    const rel = isInternalDomain({ url: restProps.href }) ? "noopener" : "noopener noreferrer";
    return (
      <a
        className={allClassNames}
        onClick={onClick}
        target={linkTarget}
        rel={rel}
        ref={ref}
        href={restProps.disabled ? "#" : href}
        {...restProps}
      >
        <Loader
          isLoading={isLoading}
          render={({ [loaderType]: type }: { [key: string]: any }) => type(loaderOptions)}
        >
          {children}
        </Loader>
      </a>
    );
  },
);

export default ExternalButton;
